/**
 * Forecast outlier rules (`im_forecast_outlier_rules`): buyer-configured rules
 * that flag demand spikes in a series' monthly sales history and cap or
 * exclude them before the forecaster fits. Shared so the contract, the engine
 * and the UI read one list. Plan: docs/procurement/forecast-outlier-rules.md.
 */

/**
 * Rule scopes, ordered most-specific first: list order is resolution
 * precedence. A series is governed by exactly one rule, the most specific
 * active one that reaches it; rules never stack.
 */
export const FORECAST_OUTLIER_SCOPES = [
  "item_loc",
  "item",
  "warehouse",
  "division",
  "organization",
] as const;
export type ForecastOutlierScope = (typeof FORECAST_OUTLIER_SCOPES)[number];

/** Which dimension columns each scope sets (and no others). */
export const FORECAST_OUTLIER_SCOPE_DIMENSIONS: Record<
  ForecastOutlierScope,
  readonly ("product" | "warehouse" | "division")[]
> = {
  item_loc: ["product", "warehouse"],
  item: ["product"],
  warehouse: ["warehouse"],
  division: ["division"],
  organization: [],
};

/**
 * How the spike threshold is computed from the series' demand months (months
 * with positive demand inside the rule's window):
 *
 *  std_dev          mean + threshold x standard deviation of the OTHER demand
 *                   months (leave-one-out, so a spike cannot inflate its own bar)
 *  median_multiple  threshold x the median demand month
 *  percentile       the threshold-th percentile of the demand months
 */
export const FORECAST_OUTLIER_METHODS = [
  "std_dev",
  "median_multiple",
  "percentile",
] as const;
export type ForecastOutlierMethod = (typeof FORECAST_OUTLIER_METHODS)[number];

/**
 * What happens to a flagged month:
 *
 *  cap      lowered to the threshold
 *  exclude  replaced by the median of the window's unflagged demand months,
 *           i.e. read as a typical month rather than as zero demand
 */
export const FORECAST_OUTLIER_ACTIONS = ["cap", "exclude"] as const;
export type ForecastOutlierAction = (typeof FORECAST_OUTLIER_ACTIONS)[number];

/** Accepted `threshold` range per method, inclusive. */
export const FORECAST_OUTLIER_THRESHOLD_BOUNDS: Record<
  ForecastOutlierMethod,
  { min: number; max: number }
> = {
  std_dev: { min: 0.5, max: 10 },
  median_multiple: { min: 1.1, max: 100 },
  percentile: { min: 50, max: 99.9 },
};

/** Trailing window, in months, a rule may evaluate. Null = the run's window. */
export const FORECAST_OUTLIER_WINDOW_MONTHS = { min: 3, max: 120 } as const;

/**
 * Fewest demand months a window must hold before any month is judged. Below
 * this there is no "typical" to be a spike against, and an intermittent series
 * with three sales would otherwise flag its largest one every run.
 */
export const FORECAST_OUTLIER_MIN_DEMAND_MONTHS = 4;

export function isForecastOutlierThresholdValid(
  method: ForecastOutlierMethod,
  threshold: number,
): boolean {
  const bounds = FORECAST_OUTLIER_THRESHOLD_BOUNDS[method];
  return (
    Number.isFinite(threshold) &&
    threshold >= bounds.min &&
    threshold <= bounds.max
  );
}

/**
 * One month an outlier rule changed, as recorded on the run input
 * (`im_forecast_run_inputs.outlier_adjustments`).
 */
export interface ForecastOutlierAdjustment {
  /** YYYY-MM-01. */
  month: string;
  /** The month's demand before the rule. */
  original: number;
  /** The demand the forecaster fitted on. */
  adjusted: number;
}
